import React, { useEffect, useMemo, useState } from 'react';
import manifestData from './data/routes-manifest.json';
import { RouteEntry, RoutesManifest } from './types/manifest';
import { SEOHead } from './components/seo/SEOHead';
import { SiteLayout } from './components/layout/SiteLayout';
import { HomeTemplate } from './templates/HomeTemplate';
import { RouteDetailTemplate } from './templates/RouteDetailTemplate';

const manifest = manifestData as unknown as RoutesManifest;

const ROUTE_ALIASES: Record<string, string> = {
  '/pmcs-ech-ecampmcs/': '/product/phan-mem-quay-video-dong-hang-ecampmcs/',
  '/product/phan-mem-quay-video-dong-goi-hang-ecampmcs/': '/product/phan-mem-quay-video-dong-hang-ecampmcs/',
  '/gioi-thieu-introduce/': '/gioi-thieu/',
  '/product-category/thiet-bi-doc-ma-vach/': '/product-category/thiet-bi-ma-vach/may-quet-ma-vach/',
  '/product-category/thiet-bi-rfid/': '/cong-nghe-rfid/',
  '/product-category/qua-tang/': '/product-category/phu-kien-cong-nghe/set-qua-tang-cong-nghe/',
  '/so-do-trang-web/': '/sitemap/',
  '/llms/': '/ai-data/',
  '/ai/': '/ai-data/'
};

function normalizePathname(rawPath: string): string {
  if (!rawPath || rawPath === '/') return '/';
  if (rawPath.endsWith('.kml') || rawPath.endsWith('.xml')) return rawPath;
  const withSlash = rawPath.endsWith('/') ? rawPath : `${rawPath}/`;
  return ROUTE_ALIASES[withSlash] || withSlash;
}

export const App: React.FC = () => {
  const [currentPath, setCurrentPath] = useState<string>(() =>
    typeof window !== 'undefined' ? normalizePathname(window.location.pathname) : '/'
  );

  useEffect(() => {
    const onPopState = () => {
      setCurrentPath(normalizePathname(window.location.pathname));
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const routeMap = useMemo(() => {
    const map = new Map<string, RouteEntry>();
    for (const r of manifest.routes) {
      const normPath = normalizePathname(r.path);
      map.set(normPath, r);
      if (r.type === 'post') {
        const slug = normPath.replace(/^\/+|\/+$/g, '').split('/').pop();
        if (slug) {
          const shortPath = `/${slug}/`;
          if (!map.has(shortPath)) {
            map.set(shortPath, r);
          }
        }
      }
    }
    return map;
  }, []);

  const handleNavigate = (nextPath: string) => {
    const norm = normalizePathname(nextPath);
    window.history.pushState({}, '', norm);
    setCurrentPath(norm);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const matchedRoute: RouteEntry = useMemo(() => {
    const found = routeMap.get(currentPath);
    if (found) return found;
    return {
      path: currentPath,
      originalUrl: `${manifest.baseUrl}${currentPath}`,
      canonicalUrl: `${manifest.baseUrl}${currentPath}`,
      type: 'page',
      sitemaps: [],
      title: `Trang không tìm thấy (404) — ${currentPath}`,
      lastmod: '',
      images: [],
      videos: [],
      indexable: false
    };
  }, [currentPath, routeMap]);

  return (
    <>
      <SEOHead route={matchedRoute} />
      <SiteLayout
        currentRoute={matchedRoute}
        manifest={manifest}
        onNavigate={handleNavigate}
      >
        {matchedRoute.path === '/' ? (
          <HomeTemplate
            manifest={manifest}
            currentRoute={matchedRoute}
            onNavigate={handleNavigate}
          />
        ) : (
          <RouteDetailTemplate
            route={matchedRoute}
            manifest={manifest}
            onNavigate={handleNavigate}
          />
        )}
      </SiteLayout>
    </>
  );
};

export default App;
